import { formatName } from '@pickleballinc/lib/format-name';
import { formatPhoneFromParts } from '@pickleballinc/lib/phone-utils';
import { PlayerSelectCardProps, PlayerSelectCardRating } from '@pickleballinc/ui/components/global/Cards/PlayerSelectCard';

import { TeamLeaguePlayerInfo } from '@/entities';
import { Country } from '@/entities/shared/lookups/Country';
import { State } from '@/entities/shared/lookups/State';
import { User } from '@/entities/SystemUsers';
import { truncateToThreeDecimals } from '@/utils/helpers';

// The substitute panels have always printed the raw `dob` (or the roster's `age`) on the age line.
const getSubAge = (player: TeamLeaguePlayerInfo | User) => ('dob' in player && player.dob) || ('age' in player && player.age) || null;

// The name, age and gender lines every substitute card shows.
export function getSubPlayerCardBaseProps(
	player: TeamLeaguePlayerInfo | User,
	avatarUrl?: string
): Pick<PlayerSelectCardProps, 'name' | 'avatarUrl' | 'age' | 'gender' | 'color'> {
	return {
		name: formatName({ firstName: player.firstName, lastName: player.lastName }),
		avatarUrl,
		age: getSubAge(player),
		gender: player.gender === 'F' ? 'Female' : 'Male',
		color: 'orange'
	};
}

// A team or event-pool player in the "Subbing in" list: the base lines plus where they live and how to reach them.
export function getSubCandidateCardProps(
	player: TeamLeaguePlayerInfo,
	avatarUrl: string | undefined,
	states: State[] | undefined,
	countries: Country[] | undefined
): Pick<PlayerSelectCardProps, 'name' | 'avatarUrl' | 'age' | 'gender' | 'color' | 'state' | 'country' | 'email' | 'phone'> {
	return {
		...getSubPlayerCardBaseProps(player, avatarUrl),
		state: states?.find((state) => parseInt(state.id.toString()) === player.stateId)?.title,
		country: countries?.find((country) => parseInt(country.id.toString()) === player.countryId)?.abbreviation,
		email: player.email,
		phone: formatPhoneFromParts({
			internationalCountryCallingCode: player?.internationalPhoneCallingCode ?? player?.country?.internationalCountryCallingCode ?? '',
			phone: player.phone ?? ''
		})
	};
}

// A rating truncated to three decimals, the way these panels print it, with "NR" for an unrated player.
export function getSubRating(label: string, value?: number | null): PlayerSelectCardRating {
	const text = truncateToThreeDecimals(value);
	return { label, value: text, rated: text !== 'NR' };
}
